import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, FileDown, ArrowUpRight, Lightbulb, HeartPulse } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { scoreColor } from "../components/shared";

function Ring({ value, size = 132, stroke = 11 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  const color = scoreColor(value);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }} transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-bold tracking-tight" style={{ color }}>{value}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
}

function band(v) {
  if (v >= 85) return { label: "Excellent", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" };
  if (v >= 70) return { label: "Good", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" };
  return { label: "Needs attention", cls: "bg-red-500/10 text-red-600 dark:text-red-400" };
}

export default function HealthCheck() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/health-check").then((r) => setData(r.data)).catch(() => toast.error("Failed to load health check")); }, []);

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const overall = data.overall;
  const overallBand = band(overall);

  return (
    <div>
      <PageHeader title="Compliance Health Check" subtitle="Live compliance scores calculated from your ISO, Training, ESG, Contractor and H&S data.">
        <Button variant="outline" className="rounded-full" onClick={() => toast.info("PDF export queued — you'll be notified when ready.")} data-testid="health-export-btn">
          <FileDown className="mr-2 h-4 w-4" /> Export PDF
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Overall */}
        <Card className="flex flex-col items-center justify-center rounded-2xl border-border p-8">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold"><HeartPulse className="h-4 w-4 text-[hsl(var(--accent))]" /> Overall Compliance</div>
          <Ring value={overall} size={200} stroke={16} />
          <span className={`mt-5 rounded-full px-4 py-1.5 text-sm font-semibold ${overallBand.cls}`}>{overallBand.label}</span>
        </Card>

        {/* Module rings */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2">
          {data.modules.map((m, i) => {
            const b = band(m.score);
            return (
              <motion.div key={m.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Link to={m.route}>
                  <Card className="group flex h-full items-center gap-4 rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`health-module-${m.key}`}>
                    <Ring value={m.score} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-heading text-sm font-bold">{m.label}</div>
                        <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                      <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${b.cls}`}>{b.label}</span>
                      <div className="mt-3 space-y-1">
                        {m.metrics.map((mt) => (
                          <div key={mt.label} className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{mt.label}</span>
                            <span className="font-semibold">{mt.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      <Card className="mt-4 rounded-2xl border-border p-6">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold"><Lightbulb className="h-4 w-4 text-amber-500" /> Recommended Actions</div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {data.recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm" data-testid={`health-rec-${i}`}>
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--accent))]/10 text-[11px] font-bold text-[hsl(var(--accent))]">{i + 1}</span>
              {r}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
